// Store de auth — reage a login/logout. Consumido por components via
// useAuthStore (Zustand) e acessado imperativamente pelo interceptor HTTP
// (`useAuthStore.getState().logout()`) para forcar logout quando refresh
// falha.
//
// Espelha o mobile (`grana-money-app/src/shared/auth/auth.store.ts`).
// Diferenca web: usa devtools middleware (Redux DevTools extension) em DEV
// para debug; mobile nao tem essa integracao.

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { tokenManager } from './token-manager';

import type { User } from '@/features/auth/types/auth.types';
import { queryClient } from '@/shared/api/query-client';
import { ENV } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';
import { setSentryUser } from '@/shared/observability/sentry-setup';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  // true durante restoreSession no boot. AppLayout usa para nao renderizar
  // shell autenticado antes da sessao ser resolvida (evita flash de rota
  // protegida).
  isLoading: boolean;
  setUser: (user: User) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => {
        set({ user, isAuthenticated: true, isLoading: false }, false, 'setUser');
        logger.auth.info('User authenticated:', user.email);
        setSentryUser({ id: user.id, email: user.email });
      },

      setLoading: (isLoading) => set({ isLoading }, false, 'setLoading'),

      // Idempotente: chamadas repetidas em sequencia (ex: interceptor +
      // useLogout.onSettled) nao quebram. Cada chamada garante tokens
      // limpos, query cache limpo, state em "deslogado" — pula
      // `logger.auth.info` se ja estava deslogado para nao poluir logs.
      logout: () => {
        const wasLoggedIn = get().isAuthenticated || get().user !== null;
        tokenManager.clearTokens();
        queryClient.clear();
        set({ user: null, isAuthenticated: false, isLoading: false }, false, 'logout');
        setSentryUser(null);
        if (wasLoggedIn) {
          logger.auth.info('User logged out');
        }
      },
    }),
    { name: 'AuthStore', enabled: ENV.IS_DEV },
  ),
);
